import { removeFromStorage } from '@/utils/helper';
import { DEFAULT_STORE_CODE } from '@/constants';
import {
  PRODUCT,
  PRODUCT_CATEGORIES,
  PRODUCT_ATTRIBUTES,
  STOCK_ITEMS,
  INVENTORY,
} from './endpoints';
import logger from './logger';
import storage from './storage';
import { toast } from 'react-toastify';
import { deleteCookie } from 'cookies-next';

const BASE_API_URL = process.env.BASE_API_URL;

const enum GLOBAL {
  MOBILE_LOGIN = 'mobile-login',
  CUSTOMERS = 'customers',
  GRAPHQL = 'graphql',
  COME_WRITE_YOURS = 'come-write-yours',
}

const LIST_ROUTES_NEED_ADMIN_TOKEN = [
  PRODUCT,
  PRODUCT_CATEGORIES,
  PRODUCT_ATTRIBUTES,
  STOCK_ITEMS,
  INVENTORY,
];

function addToken({ url, options }: any) {
  let token = null;

  if (options?.token) {
    token = options?.token;
  } else if (LIST_ROUTES_NEED_ADMIN_TOKEN.some(s => url.includes(s))) {
    token = process.env.ADMIN_TOKEN;
  } else {
    token = storage.get('token');
  }

  const config = {
    ...options,
    headers: url.includes(GLOBAL.COME_WRITE_YOURS)
      ? {
          Accept: 'application/json',
          ...(options?.headers || {}),
        }
      : {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(options?.headers || {}),
        },
  };

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else delete config.headers['Authorization'];

  return config;
}

async function urlBasedRegion(key: string, options: any) {
  let updated: string | null = null;

  if (options?.doNotIncludeRegion) {
    updated = `${BASE_API_URL}/rest/V1/${key}`;
    return updated;
  }

  if (key.includes(GLOBAL.CUSTOMERS)) {
    // no region required
    if (!key.includes('me')) {
      updated = `${BASE_API_URL}/rest/V1/${key}`;
      return updated;
    }
  }

  if (key.includes(GLOBAL.GRAPHQL)) {
    // Graphql request no region required
    updated = `${BASE_API_URL}/${key}`;
    return updated;
  }

  let region = storage?.get('store_code') || DEFAULT_STORE_CODE;

  if (options?.store_code) {
    region = options?.store_code;
  }
  if (key.includes('event')) {
    region = storage?.get('product_website') || DEFAULT_STORE_CODE;
  }

  updated = `${BASE_API_URL}/rest/${region}/V1/${key}`;

  if (key.includes('carts/mine')) {
    const quote_id = options?.quote_id || storage?.get('quote_id');
    if (quote_id) {
      updated = updated.replace('carts/mine', `customer/carts/${quote_id}`);
    }
  }

  return updated;
}

async function request(key: string, options?: any): Promise<any> {
  try {
    const url = await urlBasedRegion(key, options);
    const reqOptions = addToken({ url, options });
    const response = await fetch(url, reqOptions);
    const data: any = await response.json();
    if (
      data?.message === "The consumer isn't authorized to access %resources."
    ) {
      if (storage.get('token')) {
        toast.error('Please log in again your session has been expired!');
      }
      setTimeout(() => {
        removeFromStorage(['token', 'quote_id']);
        deleteCookie('token');
        deleteCookie('quote_id');
      }, 1000);
    }
    return { data, response, reqOptions };
  } catch (error: any) {
    logger.error(error);
    return { error };
  }
}

const http = {
  get: (url: string, options?: any) => request(url, options),
  post: (url: string, body: any, options?: any) =>
    request(url, { ...options, method: 'POST', body: JSON.stringify(body) }),
  postFormData: (url: string, body: any, options?: any) =>
    request(url, { ...options, method: 'POST', body: body }),
  put: (url: string, body?: any, options?: any) =>
    request(url, {
      method: 'PUT',
      ...(body && { body: JSON.stringify(body) }),
      ...options,
    }),
  delete: (url: string, body?: any, options?: any) =>
    request(url, {
      method: 'DELETE',
      ...(body && { body: JSON.stringify(body) }),
      ...options,
    }),
};

export default http;
